import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { useT } from '../../i18n';
import { defaultDeviceName } from '../../lib/device';
import { getServerUrl, isNative } from '../../native/platform';
import { useSession } from '../../store/session';
import { ApiError, NetworkError } from '../../sync/api';
import controls from '../controls.module.css';
import styles from './Auth.module.css';
import { AuthLayout, Field } from './AuthLayout';

/** Sign-in, or the admin creation form when the server has no account yet. */
export function Login() {
  const t = useT();
  const navigate = useNavigate();
  const status = useSession((s) => s.status);
  const meta = useSession((s) => s.meta);
  const offline = useSession((s) => s.offline);
  const login = useSession((s) => s.login);
  const setup = useSession((s) => s.setup);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [deviceName, setDeviceName] = useState(defaultDeviceName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstRun = meta !== null && !meta.setupDone;

  if (status === 'authed') return <Navigate to="/" replace />;
  if (isNative && !getServerUrl()) return <Navigate to="/server" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const creds = { username: username.trim().toLowerCase(), password, deviceName: deviceName.trim() };
      if (firstRun) await setup(creds);
      else await login(creds);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(t.error(err.code));
      else if (err instanceof NetworkError) setError(t.t('auth.serverOffline'));
      else setError(t.error(undefined));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={firstRun ? t.t('auth.setupHint') : undefined}>
      {isNative ? (
        <p className={styles.footer} style={{ textAlign: 'left' }}>
          {t.t('server.current', { url: getServerUrl() ?? '' })}{' '}
          <Link to="/server" className={styles.link}>
            {t.t('server.change')}
          </Link>
        </p>
      ) : null}
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        {firstRun ? <h2 className="display" style={{ margin: 0, fontSize: 20 }}>{t.t('auth.setupTitle')}</h2> : null}
        {offline && meta === null ? <p className={styles.hint}>{t.t('auth.serverOffline')}</p> : null}
        <Field label={t.t('auth.username')}>
          <input className={controls.field} value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} required />
        </Field>
        <Field label={t.t('auth.password')}>
          <input className={controls.field} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={firstRun ? 'new-password' : 'current-password'} required />
        </Field>
        <Field label={t.t('auth.device')} hint={t.t('auth.deviceHint')}>
          <input className={controls.field} value={deviceName} onChange={(e) => setDeviceName(e.target.value)} required />
        </Field>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        <button type="submit" className={`${controls.btn} ${controls.primary}`} disabled={busy}>
          {busy ? t.t('auth.working') : firstRun ? t.t('auth.create') : t.t('auth.signIn')}
        </button>
      </form>
      {firstRun ? null : (
        <p className={styles.footer}>
          <Link to="/forgot" className={styles.link}>
            {t.t('forgot.link')}
          </Link>
        </p>
      )}
      {firstRun ? null : meta?.registrationOpen ? (
        <p className={styles.footer}>
          <Link to="/register" className={styles.link}>
            {t.t('auth.registerLink')}
          </Link>
        </p>
      ) : (
        <p className={styles.footer}>{t.t('auth.registerClosed')}</p>
      )}
    </AuthLayout>
  );
}
